import { useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { useScoreApi } from "../api/scoreApi";
import { useScoreContext } from "../context/ScoreContext";
import { syncEngine } from "../local/sync";
import { dumpDatabase } from "../local/db";
import { getPresenceInstanceId } from "../util/presenceInstanceId";
import { gzipJson } from "../util/gzipJson";
import { slimDumpForUpload } from "../local/slimSnapshot";
import { enqueueSheetResends } from "../local/sheetManifest";

// Card #2823 (scribe-resilience G11) — the scribe-fe half of the background
// station snapshot upload. Server half is #2929 (POST /show/:showId/
// station-snapshot/:deviceId, score-api, score-common's StationSnapshotApi).
// Decided 2026-09-20 (card description): retention is forever, server side,
// no expiry; cadence is on-change, debounced, with a floor of one upload
// every five minutes while anything is unshipped.
//
// The payload is dumpDatabase() output (the same function SyncStatus.jsx's
// manual "Dump to file" button calls, so the shapes match by construction),
// passed through slimDumpForUpload (#3701): other shows' read-cache rows and
// cached signature images are slimmed so the body stays under the Lambda
// event cap on multi-day shows. The manual export stays the whole database.
//
// deviceId reuses presence.js's instanceId (util/presenceInstanceId.js,
// #2364) rather than minting a second, parallel per-station id: the
// server's own doc comment for this route (score-api's
// station-snapshot.js) describes deviceId as "same shape as presence.js's
// instanceId" and "not an identity claim, just a namespace segment" — the
// exact same contract presence's instanceId already carries.
export const SNAPSHOT_DEBOUNCE_MS = 30 * 1000;
export const SNAPSHOT_FLOOR_MS = 5 * 60 * 1000;
const FLOOR_CHECK_INTERVAL_MS = 15 * 1000;

// Card #3012 judge round 1 -- this hook must be mounted exactly ONCE for the
// whole app (App.jsx's StationSnapshotUpload does that), because every mount
// runs its own syncEngine.subscribe, its own 30s debounce timer, and its own
// 5-minute floor: a second mount (Outbox.jsx used to mount the hook a second
// time just to get a `sendNow`) meant two independent uploaders both firing
// full-DB snapshots (kept forever, server side), and Outbox's own sendNow
// only ever reset ITS instance's timers, not the App-level one a scribe was
// actually relying on. A module-level holder for the single mounted
// instance's live `upload` closure, plus this standalone export, lets a
// consumer that isn't the mounting component (the /outbox button) trigger
// the one real uploader without mounting a second one.
let liveUpload = null;
// Card #3264 -- the same live uploader, but its promise REPORTS the outcome
// instead of swallowing it. Only the Rebuild "send snapshot first" override
// reads this: it must know the snapshot really reached the office before it
// wipes the tablet. Every other consumer keeps the fire-and-forget posture.
let liveUploadConfirmed = null;

// Resolves { ok: true, snapshotKey } only after the server accepted the snapshot
// (the generated client rejects on any 4xx/5xx/network error); snapshotKey is the
// stored object's key, which Reset tablet (#3518) records as the snapshot id. Never rejects:
// no mounted uploader, a dump failure, or a failed POST all resolve
// { ok: false }, so the caller can refuse to wipe on anything but ok.
export async function sendStationSnapshotConfirmed() {
  if (!liveUploadConfirmed) return { ok: false, reason: "noUploader" };
  try {
    const reply = await liveUploadConfirmed();
    return { ok: true, snapshotKey: reply?.data?.data?.key ?? null };
  } catch {
    return { ok: false, reason: "uploadFailed" };
  }
}

export function sendStationSnapshotNow() {
  // No mounted uploader (signed out, no active show, or App.jsx hasn't
  // mounted yet) -- a harmless no-op, same posture as every other
  // fire-and-forget path here.
  liveUpload?.();
}

// Same fire-and-forget posture as usePresenceHeartbeat.js: every failure —
// a synchronous throw, a rejected promise (4xx/5xx/network) — is swallowed
// right here. This never throws into a render, never shows a toast/banner,
// never touches syncEngine.setAuthFailureHandler (a failed snapshot must
// never trip AuthGate's full-screen unmount, #1289, while a scribe is
// mid-ride), and never enters the scoring outbox itself — C4/C17's outbox
// and ship log stay the source of truth; this is a belt (card description),
// never a blocking path (kiosk doctrine: scribe-fe must never surface an
// error that interrupts scoring).
export function useStationSnapshotUpload() {
  const scoreApi = useScoreApi();
  const auth = useAuth();
  const { selectedShow } = useScoreContext();
  const showId = selectedShow?.id ?? null;
  const isAuthenticated = Boolean(auth.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated || !showId) {
      liveUpload = null;
      liveUploadConfirmed = null;
      return undefined;
    }

    let debounceTimer = null;
    // Baseline "last upload" at mount time (not 0/epoch): the floor exists
    // to force a re-upload after a long stretch of continuous, still-
    // unshipped changes, not to fire immediately on every fresh mount
    // before the debounced initial upload has even had a chance to run.
    let lastUploadAt = Date.now();
    let unshipped = false;
    let disposed = false;
    // #2823 judge round 1 (AC1) — sync.js's flush loop calls refreshPending
    // in its finally on every flushIntervalMs (5s) tick regardless of
    // whether anything actually changed (see sync.js's own comment near its
    // refreshPending definition), so syncEngine.subscribe's callback fires
    // every 5s even with a fully idle, unchanged outbox. Rescheduling the
    // 30s debounce on every one of those ticks means it can never elapse
    // while the flush loop is running -- a dead debounce that only ever
    // "fires" via the floor. Track the last snapshot's relevant fields and
    // only reschedule when one of them actually changed.
    let lastSnapshotKey = null;
    const snapshotKey = (s) =>
      `${s.pendingSync}|${s.errorSync}|${s.terminalSync}|${s.heldSync}|${s.blockedSync}`;

    // Card #3264 -- the upload itself, returning its promise (rejecting on
    // failure) so the Rebuild override can confirm delivery. `upload` below
    // is the unchanged fire-and-forget wrapper every other caller uses.
    const uploadConfirmed = () => {
      if (disposed) return Promise.reject(new Error("uploader disposed"));
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
      lastUploadAt = Date.now();
      return Promise.resolve()
        .then(async () => {
          const dump = slimDumpForUpload(await dumpDatabase(), showId);
          const { body, gzipped } = await gzipJson(dump);
          // The generated client (score-common) types requestBody as a
          // plain object and always runs it through serializeDataIfNeeded
          // (JSON.stringify), so it can't be handed a pre-gzipped body
          // directly. Passing a custom axios `transformRequest` through the
          // client's own `options` param (never a raw fetch) replaces that
          // JSON.stringify step for this one call: axios still resolves
          // `data` to the stringified dump first (serializeDataIfNeeded),
          // then hands it to our transformRequest, which ignores that and
          // returns the already-compressed bytes instead. `requestBody`
          // itself is still the real dump object either way, so a caller
          // (or a test) reading the args sees the same payload dumpDatabase()
          // produced, whether or not compression happened.
          const options = gzipped
            ? {
                headers: { "Content-Encoding": "gzip" },
                transformRequest: [() => body],
              }
            : undefined;
          const reply = await scoreApi.stationSnapshot.postStationSnapshot(
            showId,
            getPresenceInstanceId(),
            dump,
            options,
          );
          // Card #3334 (AC6) -- the snapshot reply lists sheets this station
          // shipped that the server does not hold (MISSING only, never
          // stale; ids are the original ship-log server ids). Re-send each
          // from the live local copy, the same as the manifest check does.
          // Best-effort: a failure here must never fail the upload.
          const missing = reply?.data?.data?.missingSheets;
          if (Array.isArray(missing) && missing.length > 0) {
            await enqueueSheetResends(missing).catch(() => {});
          }
          return reply;
        });
    };
    const upload = () => {
      uploadConfirmed().catch(() => {
        // Fire-and-forget: silent, full stop.
      });
    };
    // Card #3012 -- stamped right after `upload` is defined, so
    // sendStationSnapshotNow() always calls the closure that's actually
    // live for this mount's current showId/scoreApi.
    liveUpload = upload;
    liveUploadConfirmed = uploadConfirmed;

    const scheduleDebounced = () => {
      if (disposed) return;
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(upload, SNAPSHOT_DEBOUNCE_MS);
    };

    // syncEngine.subscribe invokes fn once immediately with the current
    // snapshot (sync.js), so mounting already schedules a first debounced
    // upload; every subsequent outbox change (enqueue, flush success/error,
    // online transition) reschedules it the same way.
    const unsubscribe = syncEngine.subscribe((snapshot) => {
      unshipped = snapshot.pendingSync > 0;
      const key = snapshotKey(snapshot);
      if (key === lastSnapshotKey) return;
      lastSnapshotKey = key;
      scheduleDebounced();
    });

    // The floor: forces an upload every SNAPSHOT_FLOOR_MS even if outbox
    // changes keep resetting the debounce timer before it ever fires, but
    // ONLY while something is actually unshipped (card decision) — once the
    // outbox drains, the debounced on-change upload already covers the
    // "now empty" state and there is nothing left to force a floor over.
    const floorId = setInterval(() => {
      if (unshipped && Date.now() - lastUploadAt >= SNAPSHOT_FLOOR_MS) {
        upload();
      }
    }, FLOOR_CHECK_INTERVAL_MS);

    return () => {
      disposed = true;
      liveUpload = null;
      liveUploadConfirmed = null;
      unsubscribe();
      if (debounceTimer) clearTimeout(debounceTimer);
      clearInterval(floorId);
    };
  }, [scoreApi, showId, isAuthenticated]);
}

export default useStationSnapshotUpload;
